import { useEffect, useMemo, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { LogIn, Eye, EyeOff, User, Shield, Wrench, Search, UserCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/modules/auth/AuthContext'
import { extractErrorMessage } from '@/api/http/errors'
import { cn } from '@/lib/utils'

const roleAccounts = [
  { email: 'admin@dhub.local', role: 'Администратор', icon: Shield, color: 'text-accent' },
  { email: 'maria@dhub.local', role: 'Инженер', icon: Wrench, color: 'text-green' },
  { email: 'lesha@dhub.local', role: 'Практикант', icon: Search, color: 'text-orange' },
  { email: 'elena@dhub.local', role: 'Бухгалтер', icon: UserCheck, color: 'text-text-muted' },
  { email: 'oksana@dhub.local', role: 'Схемотехник', icon: User, color: 'text-text-muted' },
]

export function LoginPage() {
  const navigate = useNavigate()
  const { state, login, loginWith2FA } = useAuth()
  const requires2FA = state.status === '2fa-required'
  const available2FAMethods = useMemo(
    () => state.status === '2fa-required' ? state.methods : ['totp'],
    [state],
  )
  const showQuickLogin = !import.meta.env.PROD

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [twoFactor, setTwoFactor] = useState('')
  const [twoFactorMethod, setTwoFactorMethod] = useState<'totp' | 'backup'>('totp')
  const [trustDevice, setTrustDevice] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')

  const apiOnline = true

  useEffect(() => {
    if (!requires2FA) return
    if (available2FAMethods.includes(twoFactorMethod)) return
    if (available2FAMethods.includes('totp')) {
      setTwoFactorMethod('totp')
      return
    }
    if (available2FAMethods.includes('backup')) {
      setTwoFactorMethod('backup')
    }
  }, [available2FAMethods, requires2FA, twoFactorMethod])

  const fillAccount = (e: string, p: string) => {
    setEmail(e)
    setPassword(p)
    setShowPassword(false)
    setError('')
    setEmailError('')
    setPasswordError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setEmailError('')
    setPasswordError('')

    if (requires2FA) {
      if (!twoFactor) { setError('Введите код 2FA'); return }
      setLoading(true)
      try {
        await loginWith2FA(twoFactor.trim(), twoFactorMethod, twoFactorMethod === 'totp' ? trustDevice : false)
        navigate('/catalog', { replace: true })
      } catch (err) {
        setError(extractErrorMessage(err, 'Ошибка 2FA'))
      } finally {
        setLoading(false)
      }
      return
    }

    if (!email.trim()) { setEmailError('Введите email'); return }
    if (!password) { setPasswordError('Введите пароль'); return }

    setLoading(true)
    try {
      await login(email, password)
      navigate('/catalog', { replace: true })
    } catch (err) {
      setError(extractErrorMessage(err, 'Ошибка входа'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-dvh p-4">
      <div className="flex flex-col md:flex-row gap-6 max-w-2xl w-full">
        <div className="flex-1 max-w-sm mx-auto w-full">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-[#8aa4ff] text-white text-base font-bold mb-4">
              DH
            </div>
            <h1 className="text-xl font-semibold text-text">Data Hub</h1>
            <p className="text-sm text-text-muted mt-1">Войдите в систему</p>
          </div>

          <div className="rounded-xl border border-border bg-surface p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-text">
                  Email <span className="text-red">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="mail@example.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError('') }}
                  aria-invalid={!!emailError}
                />
                {emailError && (
                  <p className="text-xs text-red mt-1">{emailError}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-text">
                  Пароль <span className="text-red">*</span>
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="··········"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPasswordError('') }}
                    aria-invalid={!!passwordError}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={2} /> : <Eye className="w-4 h-4" strokeWidth={2} />}
                  </button>
                </div>
                {passwordError && (
                  <p className="text-xs text-red mt-1">{passwordError}</p>
                )}
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-accent hover:text-accent/70 transition-colors">
                    Забыли пароль?
                  </Link>
                </div>
              </div>

              {requires2FA && (
                <div className="space-y-1.5">
                  {available2FAMethods.length > 1 && (
                    <div className="flex gap-2">
                      {available2FAMethods.includes('totp') && (
                        <button
                        type="button"
                        className={cn(
                          'px-3 py-1.5 rounded-md text-sm border cursor-pointer transition-colors',
                          twoFactorMethod === 'totp' ? 'border-accent bg-accent/10 text-text' : 'border-border text-text-muted',
                        )}
                        onClick={() => setTwoFactorMethod('totp')}
                      >
                        Приложение
                        </button>
                      )}
                      {available2FAMethods.includes('backup') && (
                        <button
                        type="button"
                        className={cn(
                          'px-3 py-1.5 rounded-md text-sm border cursor-pointer transition-colors',
                          twoFactorMethod === 'backup' ? 'border-accent bg-accent/10 text-text' : 'border-border text-text-muted',
                        )}
                        onClick={() => setTwoFactorMethod('backup')}
                      >
                        Резервный код
                        </button>
                      )}
                    </div>
                  )}
                  <label className="text-sm font-medium text-text">Код 2FA</label>
                  <Input
                    type="text"
                    placeholder={twoFactorMethod === 'backup' ? 'abcd-1234' : '000000'}
                    maxLength={twoFactorMethod === 'backup' ? 9 : 6}
                    value={twoFactor}
                    onChange={(e) => {
                      const value = twoFactorMethod === 'backup'
                        ? e.target.value.toLowerCase().replace(/[^a-f0-9-]/g, '')
                        : e.target.value.replace(/\D/g, '')
                      setTwoFactor(value)
                    }}
                  />
                  <p className="text-xs text-text-muted">
                    {twoFactorMethod === 'backup'
                      ? 'Введите один из резервных кодов'
                      : 'Введите код из приложения-аутентификатора'}
                  </p>
                  {twoFactorMethod === 'totp' && (
                    <label className="flex items-center gap-2 text-xs text-text-muted">
                      <input
                        type="checkbox"
                        checked={trustDevice}
                        onChange={(e) => setTrustDevice(e.target.checked)}
                      />
                      Запомнить это устройство на 30 дней
                    </label>
                  )}
                </div>
              )}

              {error && (
                <p className="text-sm text-red text-center">{error}</p>
              )}

              <Button type="submit" className="w-full gap-2" size="lg" disabled={loading}>
                <LogIn className="w-4 h-4" strokeWidth={2} />
                {loading ? 'Вход...' : requires2FA ? 'Подтвердить' : 'Войти'}
              </Button>
            </form>
          </div>

          <p className="text-xs text-text-muted text-center mt-4">
            Нет аккаунта?{' '}
            <Link to="/register" className="text-accent hover:text-accent/70 transition-colors">
              Зарегистрироваться
            </Link>
          </p>

          <div className="flex items-center justify-center gap-2 mt-6">
            <span className={cn(
              'w-2 h-2 rounded-full',
              apiOnline ? 'bg-green shadow-[0_0_6px_#4caf78]' : 'bg-orange',
            )} />
            <span className="text-xs text-text-muted">
              {apiOnline ? 'Сервер доступен' : 'Нет соединения'}
            </span>
          </div>
        </div>

        {showQuickLogin && (
          <div className="w-full md:w-56 shrink-0">
            <div className="sticky top-4 space-y-3">
              <h3 className="text-sm font-medium text-text">Быстрый вход</h3>
              <p className="text-xs text-text-muted">Нажмите на роль для автозаполнения</p>
              <div className="flex md:flex-col gap-2 flex-wrap">
                {roleAccounts.map((acc) => {
                  const Icon = acc.icon
                  const active = email === acc.email
                  return (
                    <button
                      key={acc.email}
                      className={cn(
                        'flex items-center gap-2.5 w-full px-3.5 py-2.5 rounded-lg text-left transition-all cursor-pointer',
                        active
                          ? 'bg-accent/15 border border-accent/30'
                          : 'bg-surface border border-border hover:border-accent/30 hover:bg-accent-soft/20',
                      )}
                      onClick={() => fillAccount(acc.email, acc.email === 'admin@dhub.local' ? 'Admin123!' : acc.email === 'maria@dhub.local' ? 'Maria123!' : acc.email === 'lesha@dhub.local' ? 'Lesha123!' : acc.email === 'elena@dhub.local' ? 'Elena123!' : 'Oksana123!')}
                    >
                      <Icon className={cn('w-4 h-4 shrink-0', acc.color)} strokeWidth={2} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-text truncate">{acc.role}</p>
                        <p className="text-[10px] text-text-muted truncate">{acc.email}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
